import axios from 'axios';
import React, { useEffect, useState } from 'react';
import { NavLink } from 'react-router-dom';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import MuiMenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';
import ManageAccountsOutlinedIcon from '@mui/icons-material/ManageAccountsOutlined';
import AdminPanelSettingsOutlinedIcon from '@mui/icons-material/AdminPanelSettingsOutlined';
import { EmptyState, ListSkeleton, PageHeader } from '@routes-plan/ui';
import { Role } from '../../../models/role';
import PermissionButton from '../../../components/common/PermissionButton';
import { useResourcePermissions } from '../../../hooks/useResourcePermissions';
import { setPageTitle } from '../../../utils/pageTitle';
import { ScreenTourButton } from '../../../components/help/ScreenHelp';
import { useT } from '../../../i18n';
import { AdminPage, ContentCard, FilterRow, FormActions, useAlert } from '../shared/AdminUi';
import RoleModal from './modal/RoleModal';
import { accessErrorMessage } from '../../../utils/accessErrors';

interface ShowAlerts {
  showAlert: (message: string, severity?: 'success' | 'info' | 'warning' | 'error') => void;
  showLoading: () => void;
  hideLoading: () => void;
}
interface PermissionItem {
  id: number;
  name: string;
  action: string;
  checked: boolean;
}

interface MenuItem {
  id: number;
  name: string;
  permissions: PermissionItem[];
}

interface ModuleItem {
  id: number;
  name: string;
  menus: MenuItem[];
}

const dedupePermissions = <T extends { action?: string; name?: string }>(permissions: T[] = []) => {
  const seenKeys = new Set<string>();

  return permissions.filter((permission) => {
    const actionKey = String(permission.action || '').toLowerCase().trim();
    const nameKey = String(permission.name || '').toLowerCase().trim();
    const dedupeKey = actionKey || nameKey;

    if (!dedupeKey || seenKeys.has(dedupeKey)) {
      return false;
    }

    seenKeys.add(dedupeKey);
    return true;
  });
};

const PermissionList: React.FC<ShowAlerts> = ({ showLoading, hideLoading }) => {
  setPageTitle('menu.admin.permissions');
  const t = useT();
  const showAlert = useAlert();
  const [saving, setSaving] = useState(false);
  const [roles, setRoles] = useState([]);
  const [roleId, setRoleId] = useState('');
  const [modules, setModules] = useState<ModuleItem[]>([]);
  const [openModal, setOpenModal] = useState(false);
  const [isLoadingRoles, setIsLoadingRoles] = useState(true);
  const [isLoadingPermissions, setIsLoadingPermissions] = useState(false);
  const { canCreate, canUpdate, canDelete } = useResourcePermissions('/admin/permissions');
  const canManagePrivileges = canUpdate || canCreate;
  const canManageMenus = canCreate || canUpdate || canDelete;

  useEffect(() => {
    const fetchData = async () => {
      setIsLoadingRoles(true);
      try {
        const [rolesResponse] = await Promise.all([
          axios.get('roles')
        ]);
        setRoles(rolesResponse.data);
      } catch (error) {
        showAlert(t('permissions.rolesLoadError'), 'error');
      } finally {
        setIsLoadingRoles(false);
      }
    };
    fetchData();
  }, [showAlert, t]);
  useEffect(() => {
    const fetchData = async () => {
      setIsLoadingPermissions(true);
      try {
        const response = await axios.get(`roles/${roleId}/permissions`);
        const sanitizedModules = (response.data || []).map((module: ModuleItem) => ({
          ...module,
          menus: (module.menus || []).map((menu) => ({
            ...menu,
            permissions: dedupePermissions(menu.permissions || []),
          })),
        }));
        setModules(sanitizedModules);
      } catch (error) {
        showAlert(t('permissions.permissionsLoadError'), 'error');
      } finally {
        setIsLoadingPermissions(false);
      }
    };
    if (roleId) fetchData();
  }, [roleId, showAlert, t]);

  const handleCheckboxChange = (permissionId: number) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const updatedModules = modules.map(module => ({
      ...module,
      menus: module.menus.map(menu => ({
        ...menu,
        permissions: menu.permissions.map(permission =>
          permission.id === permissionId
            ? { ...permission, checked: event.target.checked }
            : permission
        )
      }))
    }));
    setModules(updatedModules);
  };
  const handleSavePermissions = async () => {
    if (!canManagePrivileges) return;

    const selectedPermissions = modules
      .flatMap(module => module.menus)
      .flatMap(menu => menu.permissions)
      .filter(permission => permission.checked)
      .map(permission => permission.id);

    setSaving(true);
    try {
      await axios.put(`roles/${roleId}/permissions`, {
        permissionIds: selectedPermissions
      });
      showAlert(t('permissions.savedOk'), "success");
    } catch (error) {
      console.error(error);
      showAlert(accessErrorMessage(error) ?? t('permissions.saveError'), "error");
    } finally {
      setSaving(false);
    }
  };

  const handleModuleToggle = (moduleId: number, checked: boolean) => {
    const updatedModules = modules.map(module => {
      if (module.id !== moduleId) return module;

      return {
        ...module,
        menus: module.menus.map(menu => ({
          ...menu,
          permissions: menu.permissions.map(permission => ({
            ...permission,
            checked
          }))
        }))
      };
    });

    setModules(updatedModules);
  };
  const refreshRoles = async () => {
    try {
      const rolesResponse = await axios.get("roles");
      setRoles(rolesResponse.data);
    } catch {
      showAlert(t('permissions.rolesRefreshError'), "error");
    }
  };
  const allChecked = (module: ModuleItem) => module.menus.every((menu) => menu.permissions.every((p) => p.checked));
  const someChecked = (module: ModuleItem) => module.menus.some((menu) => menu.permissions.some((p) => p.checked));

  return (
    <AdminPage>
      <PageHeader
        title={t('permissions.rolesTitle')}
        description={t('permissions.rolesSubtitle')}
        helpAction={<ScreenTourButton />}
        actions={
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <PermissionButton
              allowed={canManageMenus}
              deniedTooltip={t('permissions.noPermissionManageMenu')}
              variant="outlined"
              color="inherit"
              component={NavLink}
              to="/admin/permissions/new"
              data-tour="permissions-manage-menu"
              startIcon={<TuneOutlinedIcon />}
            >
              {t('permissions.manageMenu')}
            </PermissionButton>
            <PermissionButton
              allowed={canCreate}
              deniedTooltip={t('common.noPermissionCreate')}
              variant="contained"
              startIcon={<ManageAccountsOutlinedIcon />}
              onClick={() => setOpenModal(true)}
              data-tour="permissions-add-role"
            >
              {t('permissions.addRole')}
            </PermissionButton>
          </Box>
        }
      />
      <ContentCard>
        <FilterRow>
          <TextField
            id="role-id"
            data-tour="permissions-role"
            select
            size="small"
            label={t('permissions.selectRole')}
            value={roles.length ? roleId : ''}
            onChange={(event) => setRoleId(event.target.value)}
            disabled={isLoadingRoles}
            fullWidth
          >
            {roles.map((role: Role) => (
              <MuiMenuItem key={role.id} value={role.id}>
                {role.name}
              </MuiMenuItem>
            ))}
          </TextField>
        </FilterRow>
      </ContentCard>

      {isLoadingRoles || isLoadingPermissions ? (
        <ContentCard>
          <ListSkeleton rows={6} columns={3} />
        </ContentCard>
      ) : modules.length > 0 ? (
        <>
          {modules.map((module, moduleIndex) => (
            <Paper
              key={module.id}
              variant="outlined"
              sx={{ p: { xs: 2, md: 3 }, minWidth: 0 }}
              data-tour={moduleIndex === 0 ? 'permissions-module' : undefined}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, flexWrap: 'wrap', mb: 1.5 }}>
                <Typography variant="h6" component="h2">
                  {module.name}
                </Typography>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={allChecked(module)}
                      indeterminate={someChecked(module) && !allChecked(module)}
                      onChange={(e) => handleModuleToggle(module.id, e.target.checked)}
                    />
                  }
                  label={t('permissions.selectAll')}
                  data-tour={moduleIndex === 0 ? 'permissions-select-all' : undefined}
                />
              </Box>
              <Box
                sx={{
                  display: 'grid',
                  gap: 2,
                  gridTemplateColumns: 'repeat(auto-fill, minmax(min(220px, 100%), 1fr))',
                }}
              >
                {module.menus.map((menu, menuIndex) => (
                  <Box
                    key={menu.id}
                    sx={{ minWidth: 0, p: 1.5, borderRadius: 1, bgcolor: 'action.hover' }}
                    data-tour={moduleIndex === 0 && menuIndex === 0 ? 'permissions-menu' : undefined}
                  >
                    <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                      {menu.name}
                    </Typography>
                    {/* Ver el menú primero; luego las acciones. */}
                    {[
                      ...menu.permissions.filter((p) => p.action === 'toggle_visibility'),
                      ...menu.permissions.filter((p) => p.action !== 'toggle_visibility'),
                    ].map((permission) => (
                      <FormControlLabel
                        key={permission.id}
                        sx={{ display: 'flex', mr: 0, '& .MuiFormControlLabel-label': { fontSize: '0.875rem' } }}
                        control={<Checkbox size="small" checked={permission.checked} onChange={handleCheckboxChange(permission.id)} />}
                        label={permission.name}
                      />
                    ))}
                  </Box>
                ))}
              </Box>
            </Paper>
          ))}
          <Box
            data-tour="permissions-save"
            sx={{
              position: 'sticky',
              bottom: 0,
              zIndex: 2,
              py: 1.5,
              bgcolor: 'background.default',
              borderTop: 1,
              borderColor: 'divider',
            }}
          >
            <FormActions
              submit={false}
              onSave={handleSavePermissions}
              saving={saving}
              allowed={canManagePrivileges}
              deniedTooltip={t('common.noPermissionEdit')}
              saveLabel={t('permissions.savePermissions')}
            />
          </Box>
        </>
      ) : (
        <ContentCard>
          <EmptyState
            icon={<AdminPanelSettingsOutlinedIcon />}
            title={roleId ? t('permissions.noPermissionsForRole') : t('permissions.chooseRole')}
            description={roleId ? undefined : t('permissions.chooseRoleHint')}
            dense
          />
        </ContentCard>
      )}
      <RoleModal
        open={openModal}
        onClose={() => setOpenModal(false)}
        roles={roles}
        canCreate={canCreate}
        canDelete={canDelete}
        refreshRoles={refreshRoles}
        showAlert={showAlert}
        showLoading={showLoading}
        hideLoading={hideLoading}
      />
    </AdminPage>
  );
};

export default PermissionList;
